import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { getSupabase } from "@/lib/supabase";
import { usePurchasePrice } from "@/contexts/PurchasePriceContext";

interface Product {
  id: string;
  product_code: string;
  barcode: string;
  name: string;
  brand: string;
  model: string;
  purchase_price: number;
  sale_price: number;
  image_url: string;
}

interface IbanItem {
  id: string;
  bank_name: string;
  iban_number: string;
}

interface CurrentDealer {
  id: string;
  name: string;
  phone: string;
  balance: number;
}

interface SaleItem {
  id: string;
  productName: string;
  quantity: number;
  amount: number;
  paymentMethod: string;
  date: string;
}

export default function DashboardPage() {
  const { user } = useAuth();
  const dealerId = user?.dealerId || user?.id;

  const [summary, setSummary] = useState({ nakit: 0, pos: 0, iban: 0, cari: 0 });
  const [recentSales, setRecentSales] = useState<SaleItem[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [ibans, setIbans] = useState<IbanItem[]>([]);
  const [currentDealers, setCurrentDealers] = useState<CurrentDealer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchData = useCallback(async () => {
    if (!dealerId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");

    try {
      const supabase = getSupabase();

      const [salesRes, productsRes, ibansRes, dealersRes] = await Promise.all([
        supabase.from("sales").select("id, payment_method, total_amount, created_at").eq("dealer_id", dealerId).order("created_at", { ascending: false }).limit(10),
        supabase.from("products").select("id, product_code, barcode, name, brand, model, purchase_price, sale_price, image_url").eq("dealer_id", dealerId),
        supabase.from("ibans").select("id, bank_name, iban_number").eq("dealer_id", dealerId),
        supabase.from("current_dealers").select("id, name, phone, balance").eq("dealer_id", dealerId),
      ]);

      if (salesRes.error) throw salesRes.error;
      if (productsRes.error) throw productsRes.error;
      if (ibansRes.error) throw ibansRes.error;
      if (dealersRes.error) throw dealersRes.error;

      const sales = salesRes.data || [];
      const today = new Date().toISOString().split("T")[0];

      let nakit = 0, pos = 0, iban = 0, cari = 0;
      sales.filter((s) => s.created_at?.startsWith(today)).forEach((s) => {
        if (s.payment_method === "nakit") nakit += Number(s.total_amount);
        else if (s.payment_method === "pos") pos += Number(s.total_amount);
        else if (s.payment_method === "iban") iban += Number(s.total_amount);
        else if (s.payment_method === "cari") cari += Number(s.total_amount);
      });

      setSummary({ nakit, pos, iban, cari });

      const saleItems: SaleItem[] = sales.slice(0, 5).map((s) => ({
        id: s.id,
        productName: "Satış",
        quantity: 1,
        amount: Number(s.total_amount),
        paymentMethod: s.payment_method,
        date: s.created_at,
      }));
      setRecentSales(saleItems);

      setProducts(productsRes.data || []);
      setIbans(ibansRes.data || []);
      setCurrentDealers(dealersRes.data || []);

    } catch (err) {
      setError("Veri yüklenirken hata oluştu.");
    } finally {
      setLoading(false);
    }
  }, [dealerId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <div className="max-w-7xl mx-auto">
      {/* Page Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h4 className="text-lg font-semibold text-foreground-950">
            <a id="dashboard-title">Dashboard</a>
          </h4>
          <p className="text-sm text-foreground-500 mt-0.5">
            Hoş geldiniz, bugünün satış özetleri aşağıda listelenmiştir.
          </p>
        </div>
        <button
          onClick={fetchData}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-600 hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
        >
          <i className={`ri-refresh-line ${loading ? "animate-spin" : ""}`}></i>
          Yenile
        </button>
      </div>

      {error ? (
        <div className="bg-accent-100 border border-accent-300 text-accent-900 rounded-xl px-4 py-3 mb-6 text-sm flex items-center gap-2">
          <i className="ri-error-warning-line"></i>
          {error}
          <button onClick={fetchData} className="ml-auto text-accent-700 font-medium cursor-pointer">Tekrar Dene</button>
        </div>
      ) : null}

      {loading && !error ? (
        <div className="flex items-center justify-center py-20">
          <i className="ri-loader-4-line animate-spin text-2xl text-foreground-400"></i>
        </div>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <SummaryCard icon="ri-money-dollar-circle-line" iconBg="bg-primary-100" iconColor="text-primary-600" label="Nakit Satış" amount={`$${summary.nakit.toLocaleString("en-US", { minimumFractionDigits: 2 })}`} />
            <SummaryCard icon="ri-bank-card-line" iconBg="bg-accent-100" iconColor="text-accent-600" label="POS Satış" amount={`$${summary.pos.toLocaleString("en-US", { minimumFractionDigits: 2 })}`} />
            <SummaryCard icon="ri-bank-line" iconBg="bg-secondary-100" iconColor="text-secondary-600" label="IBAN Satış" amount={`$${summary.iban.toLocaleString("en-US", { minimumFractionDigits: 2 })}`} />
            <SummaryCard icon="ri-user-star-line" iconBg="bg-primary-100" iconColor="text-primary-600" label="Cari Satış" amount={`$${summary.cari.toLocaleString("en-US", { minimumFractionDigits: 2 })}`} />
          </div>

          {/* Action Buttons + Search */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-6">
            <div className="lg:col-span-1 space-y-3">
              <a href="/sales" className="w-full flex items-center gap-3 px-4 py-3.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-xl text-sm font-medium transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-shopping-cart-2-line text-lg"></i>
                Satış Yap
              </a>
              <a href="/dealers" className="w-full flex items-center gap-3 px-4 py-3.5 bg-background-100 border border-background-200/70 hover:bg-background-200/70 text-foreground-700 rounded-xl text-sm font-medium transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-user-star-line text-lg"></i>
                Cari Bayiler
              </a>
            </div>
            <div className="lg:col-span-3">
              <ProductSearch products={products} ibans={ibans} currentDealers={currentDealers} dealerId={dealerId || ""} onSaleComplete={fetchData} />
            </div>
          </div>

          {/* Recent Sales */}
          <RecentSales sales={recentSales} />
        </>
      )}
    </div>
  );
}

function SummaryCard({ icon, iconBg, iconColor, label, amount }: { icon: string; iconBg: string; iconColor: string; label: string; amount: string }) {
  return (
    <div className="bg-background-50 border border-background-200/70 rounded-xl p-4 flex items-start gap-3 hover:border-background-300/60 transition-colors">
      <div className={`w-10 h-10 ${iconBg} rounded-lg flex items-center justify-center shrink-0`}>
        <i className={`${icon} ${iconColor} text-lg`}></i>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-foreground-400 mb-1">{label}</p>
        <p className="text-lg font-semibold text-foreground-950 whitespace-nowrap">{amount}</p>
      </div>
    </div>
  );
}

function ProductSearch({ products, ibans, currentDealers, dealerId, onSaleComplete }: { products: Product[]; ibans: IbanItem[]; currentDealers: CurrentDealer[]; dealerId: string; onSaleComplete: () => void }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<Product[]>([]);
  const [cart, setCart] = useState<Array<{ product: Product; quantity: number }>>([]);
  const [showCart, setShowCart] = useState(false);
  const { showPurchasePrice } = usePurchasePrice();

  const handleQueryChange = (value: string) => {
    setQuery(value);
    const q = value.toLowerCase().trim();
    if (!q) {
      setResults([]);
      return;
    }
    const filtered = products.filter(
      (p) =>
        (p.name && p.name.toLowerCase().includes(q)) ||
        (p.product_code && p.product_code.toLowerCase().includes(q)) ||
        (p.barcode && p.barcode.includes(q)) ||
        (p.brand && p.brand.toLowerCase().includes(q))
    );
    setResults(filtered);
  };

  const addToCart = (product: Product) => {
    setCart((prev) => {
      const existing = prev.find((item) => item.product.id === product.id);
      if (existing) {
        return prev.map((item) =>
          item.product.id === product.id ? { ...item, quantity: item.quantity + 1 } : item
        );
      }
      return [...prev, { product, quantity: 1 }];
    });
  };

  const removeFromCart = (productId: string) => {
    setCart((prev) => prev.filter((item) => item.product.id !== productId));
  };

  const updateQuantity = (productId: string, quantity: number) => {
    if (quantity <= 0) { removeFromCart(productId); return; }
    setCart((prev) =>
      prev.map((item) => (item.product.id === productId ? { ...item, quantity } : item))
    );
  };

  const totalAmount = cart.reduce((sum, item) => sum + item.product.sale_price * item.quantity, 0);

  const handleSaleSuccess = () => {
    setCart([]);
    setShowCart(false);
    onSaleComplete();
  };

  return (
    <div className="bg-background-50 border border-background-200/70 rounded-xl p-4">
      <div className="relative">
        <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400"></i>
        <input
          type="text"
          value={query}
          onChange={(e) => handleQueryChange(e.target.value)}
          placeholder="Ürün adı, barkod veya ürün kodu ile arayın..."
          className="w-full pl-10 pr-4 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
        />
      </div>

      {results.length > 0 && (
        <div className="mt-4 space-y-2 max-h-80 overflow-y-auto">
          {results.map((product) => (
            <div
              key={product.id}
              onClick={() => addToCart(product)}
              className="flex items-center gap-3 p-3 bg-background-100 rounded-lg hover:bg-background-200/70 transition-colors cursor-pointer border border-transparent hover:border-primary-200"
            >
              {product.image_url ? (
                <img src={product.image_url} alt={product.name} title={product.name} className="w-12 h-12 rounded-lg object-cover shrink-0" />
              ) : (
                <div className="w-12 h-12 rounded-lg bg-background-50 border border-background-200/70 flex items-center justify-center shrink-0">
                  <i className="ri-image-line text-foreground-300"></i>
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground-900 truncate">{product.name}</p>
                <p className="text-xs text-foreground-400">{product.product_code} | Barkod: {product.barcode}</p>
              </div>
              <div className="text-right shrink-0">
                <p className="text-sm font-semibold text-primary-600">${product.sale_price.toLocaleString("en-US", { minimumFractionDigits: 2 })}</p>
                {showPurchasePrice && <p className="text-xs text-foreground-400">Alış: ${product.purchase_price.toLocaleString("en-US", { minimumFractionDigits: 2 })}</p>}
              </div>
              <div className="w-8 h-8 rounded-full bg-primary-500 flex items-center justify-center shrink-0">
                <i className="ri-add-line text-background-50"></i>
              </div>
            </div>
          ))}
        </div>
      )}

      {query.trim() !== "" && results.length === 0 && (
        <div className="mt-4 text-center py-8 text-foreground-400 text-sm">
          <i className="ri-search-line text-3xl block mb-2"></i>
          Aradığınız kriterlere uygun ürün bulunamadı.
        </div>
      )}

      {cart.length > 0 && (
        <div className="mt-3 pt-3 border-t border-background-200/70">
          <button
            onClick={() => setShowCart(!showCart)}
            className="flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors cursor-pointer"
          >
            <i className="ri-shopping-cart-2-line"></i>
            Sepet ({cart.length} ürün - ${totalAmount.toLocaleString("en-US", { minimumFractionDigits: 2 })})
            <i className={showCart ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}></i>
          </button>

          {showCart && (
            <CartDetail
              cart={cart}
              updateQuantity={updateQuantity}
              removeFromCart={removeFromCart}
              totalAmount={totalAmount}
              ibans={ibans}
              currentDealers={currentDealers}
              dealerId={dealerId}
              onSaleComplete={handleSaleSuccess}
            />
          )}
        </div>
      )}
    </div>
  );
}

function CartDetail({
  cart, updateQuantity, removeFromCart, totalAmount, ibans, currentDealers, dealerId, onSaleComplete,
}: {
  cart: Array<{ product: Product; quantity: number }>;
  updateQuantity: (productId: string, quantity: number) => void;
  removeFromCart: (productId: string) => void;
  totalAmount: number;
  ibans: IbanItem[];
  currentDealers: CurrentDealer[];
  dealerId: string;
  onSaleComplete: () => void;
}) {
  const [paymentMethod, setPaymentMethod] = useState<string | null>(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [saleComplete, setSaleComplete] = useState(false);
  const [selectedIban, setSelectedIban] = useState<string | null>(null);
  const [selectedDealer, setSelectedDealer] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const handleCompleteSale = () => {
    setSelectedIban(null);
    setSelectedDealer(null);
    setSubmitError("");
    setShowPaymentModal(true);
  };

  const confirmSale = async () => {
    if (!paymentMethod) return;
    if (paymentMethod === "iban" && !selectedIban) return;
    if (paymentMethod === "cari" && !selectedDealer) return;

    setSubmitting(true);
    setSubmitError("");

    try {
      const supabase = getSupabase();
      const { data: sale, error: saleErr } = await supabase
        .from("sales")
        .insert({
          dealer_id: dealerId,
          payment_method: paymentMethod,
          iban_id: paymentMethod === "iban" ? selectedIban : null,
          current_dealer_id: paymentMethod === "cari" ? selectedDealer : null,
          total_amount: totalAmount,
        })
        .select("id")
        .single();

      if (saleErr) throw saleErr;

      const saleItems = cart.map((item) => ({
        sale_id: sale.id,
        product_id: item.product.id,
        quantity: item.quantity,
        unit_price: item.product.sale_price,
        purchase_price: item.product.purchase_price,
      }));

      const { error: itemsErr } = await supabase.from("sale_items").insert(saleItems);
      if (itemsErr) throw itemsErr;

      setSaleComplete(true);
      setShowPaymentModal(false);
      onSaleComplete();
    } catch (err: any) {
      setSubmitError(err.message || "Satış kaydedilirken hata oluştu.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mt-3 space-y-2">
      {cart.map((item) => (
        <div key={item.product.id} className="flex items-center gap-3 p-2.5 bg-background-100 rounded-lg">
          <span className="text-sm text-foreground-900 flex-1 truncate">{item.product.name}</span>
          <div className="flex items-center gap-1">
            <button onClick={() => updateQuantity(item.product.id, item.quantity - 1)} className="w-7 h-7 rounded-md bg-background-50 border border-background-200/70 flex items-center justify-center text-foreground-500 hover:bg-background-200/70 cursor-pointer">
              <i className="ri-subtract-line text-xs"></i>
            </button>
            <span className="w-8 text-center text-sm font-medium text-foreground-900">{item.quantity}</span>
            <button onClick={() => updateQuantity(item.product.id, item.quantity + 1)} className="w-7 h-7 rounded-md bg-background-50 border border-background-200/70 flex items-center justify-center text-foreground-500 hover:bg-background-200/70 cursor-pointer">
              <i className="ri-add-line text-xs"></i>
            </button>
          </div>
          <span className="text-sm font-medium text-foreground-900 w-24 text-right">${(item.product.sale_price * item.quantity).toLocaleString("en-US", { minimumFractionDigits: 2 })}</span>
          <button onClick={() => removeFromCart(item.product.id)} className="w-7 h-7 rounded-md flex items-center justify-center text-foreground-400 hover:text-accent-500 hover:bg-accent-100 cursor-pointer">
            <i className="ri-delete-bin-line text-sm"></i>
          </button>
        </div>
      ))}

      <div className="flex items-center justify-between pt-2 border-t border-background-200/70">
        <span className="text-sm font-semibold text-foreground-950">Toplam</span>
        <span className="text-lg font-bold text-foreground-950">${totalAmount.toLocaleString("en-US", { minimumFractionDigits: 2 })}</span>
      </div>

      {!saleComplete ? (
        <button onClick={handleCompleteSale} className="w-full py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap">
          Satışı Tamamla
        </button>
      ) : (
        <div className="bg-primary-100 text-primary-700 rounded-lg px-4 py-2.5 text-sm text-center font-medium">
          <i className="ri-check-line mr-1"></i>Satış başarıyla tamamlandı!
        </div>
      )}

      {showPaymentModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => setShowPaymentModal(false)}>
          <div className="bg-background-50 rounded-2xl p-6 w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
            <h4 className="text-lg font-semibold text-foreground-950 mb-4"><a id="payment-method">Ödeme Yöntemi</a></h4>
            <div className="space-y-2">
              {[
                { id: "nakit", label: "Nakit", icon: "ri-money-dollar-circle-line" },
                { id: "pos", label: "POS", icon: "ri-bank-card-line" },
                { id: "iban", label: "IBAN Havale", icon: "ri-bank-line" },
                { id: "cari", label: "Cari Hesap", icon: "ri-user-star-line" },
              ].map((method) => (
                <button
                  key={method.id}
                  onClick={() => { setPaymentMethod(method.id); setSelectedIban(null); setSelectedDealer(null); }}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors cursor-pointer ${paymentMethod === method.id ? "bg-primary-100 border border-primary-200 text-primary-700" : "bg-background-100 border border-background-200/70 text-foreground-600 hover:bg-background-200/70"}`}
                >
                  <i className={`${method.icon} text-lg`}></i>
                  {method.label}
                  {paymentMethod === method.id && <i className="ri-check-line ml-auto"></i>}
                </button>
              ))}
            </div>

            {paymentMethod === "iban" && (
              <div className="mt-3 mb-3">
                <p className="text-xs font-medium text-foreground-500 mb-2">İşlem yapılacak IBAN'ı seçin</p>
                <div className="space-y-1.5">
                  {ibans.map((iban) => (
                    <button
                      key={iban.id}
                      onClick={() => setSelectedIban(iban.id)}
                      className={`w-full text-left p-2.5 rounded-lg text-xs transition-colors cursor-pointer ${selectedIban === iban.id ? "bg-primary-100 border border-primary-200" : "bg-background-100 border border-background-200/70 hover:bg-background-200/70"}`}
                    >
                      <span className="font-medium text-foreground-700">{iban.bank_name}</span>
                      <span className="text-foreground-400 ml-2">{iban.iban_number}</span>
                      {selectedIban === iban.id && <i className="ri-check-line text-primary-600 float-right mt-0.5"></i>}
                    </button>
                  ))}
                </div>
                {selectedIban === null && <p className="text-xs text-accent-500 mt-1">Lütfen bir IBAN seçin</p>}
              </div>
            )}

            {paymentMethod === "cari" && (
              <div className="mt-3 mb-3">
                <p className="text-xs font-medium text-foreground-500 mb-2">Cari bayi seçin</p>
                <div className="space-y-1.5 max-h-40 overflow-y-auto">
                  {currentDealers.map((dealer) => (
                    <button
                      key={dealer.id}
                      onClick={() => setSelectedDealer(dealer.id)}
                      className={`w-full text-left p-2.5 rounded-lg text-xs transition-colors cursor-pointer ${selectedDealer === dealer.id ? "bg-primary-100 border border-primary-200" : "bg-background-100 border border-background-200/70 hover:bg-background-200/70"}`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-foreground-700">{dealer.name}</span>
                        <span className="text-foreground-400">Bakiye: ${dealer.balance.toLocaleString("en-US", { minimumFractionDigits: 2 })}</span>
                      </div>
                      {selectedDealer === dealer.id && <i className="ri-check-line text-primary-600 float-right"></i>}
                    </button>
                  ))}
                </div>
                {selectedDealer === null && <p className="text-xs text-accent-500 mt-1">Lütfen bir cari bayi seçin</p>}
              </div>
            )}

            {submitError && <div className="bg-accent-100 text-accent-700 text-xs rounded-lg px-3 py-2 mt-3">{submitError}</div>}

            <button
              onClick={confirmSale}
              disabled={!paymentMethod || (paymentMethod === "iban" && !selectedIban) || (paymentMethod === "cari" && !selectedDealer) || submitting}
              className="w-full mt-4 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
            >
              {submitting ? "Kaydediliyor..." : `Satışı Onayla ($${totalAmount.toLocaleString("en-US", { minimumFractionDigits: 2 })})`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function RecentSales({ sales }: { sales: SaleItem[] }) {
  return (
    <div className="bg-background-50 border border-background-200/70 rounded-xl">
      <div className="px-4 py-3 border-b border-background-200/70 flex items-center justify-between">
        <h4 className="font-medium text-foreground-900 text-sm"><a id="recent-sales">Bugünkü Satışlar</a></h4>
        <a href="/reports" className="text-xs text-primary-600 hover:text-primary-700 font-medium">Tümünü Gör</a>
      </div>
      <div className="overflow-x-auto">
        {sales.length === 0 ? (
          <div className="text-center py-8 text-foreground-400 text-sm">Henüz satış kaydı bulunmuyor.</div>
        ) : (
          <>
            {/* Desktop Table */}
            <table className="w-full text-sm min-w-[500px] hidden md:table">
              <thead>
                <tr className="bg-background-100 text-left">
                  <th className="px-4 py-2.5 text-xs font-medium text-foreground-500">Ürün</th>
                  <th className="px-4 py-2.5 text-xs font-medium text-foreground-500">Adet</th>
                  <th className="px-4 py-2.5 text-xs font-medium text-foreground-500">Tutar</th>
                  <th className="px-4 py-2.5 text-xs font-medium text-foreground-500">Ödeme</th>
                  <th className="px-4 py-2.5 text-xs font-medium text-foreground-500">Saat</th>
                </tr>
              </thead>
              <tbody>
                {sales.map((sale) => (
                  <tr key={sale.id} className="border-t border-background-200/70 hover:bg-background-100/50 transition-colors">
                    <td className="px-4 py-3 text-foreground-900 font-medium">{sale.productName}</td>
                    <td className="px-4 py-3 text-foreground-600">{sale.quantity}</td>
                    <td className="px-4 py-3 text-foreground-900 font-medium">${sale.amount.toLocaleString("en-US", { minimumFractionDigits: 2 })}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${sale.paymentMethod === "nakit" ? "bg-primary-100 text-primary-700" : sale.paymentMethod === "pos" ? "bg-accent-100 text-accent-700" : sale.paymentMethod === "iban" ? "bg-secondary-100 text-secondary-700" : "bg-primary-100 text-primary-700"}`}>
                        {sale.paymentMethod === "nakit" ? "Nakit" : sale.paymentMethod === "pos" ? "POS" : sale.paymentMethod === "iban" ? "IBAN" : "Cari"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-foreground-400 text-xs">
                      {new Date(sale.date).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Mobile Card View */}
            <div className="md:hidden divide-y divide-background-200/70">
              {sales.map((sale) => (
                <div key={sale.id} className="px-4 py-3 flex items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground-900 truncate">{sale.productName}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${sale.paymentMethod === "nakit" ? "bg-primary-100 text-primary-700" : sale.paymentMethod === "pos" ? "bg-accent-100 text-accent-700" : sale.paymentMethod === "iban" ? "bg-secondary-100 text-secondary-700" : "bg-primary-100 text-primary-700"}`}>
                        {sale.paymentMethod === "nakit" ? "Nakit" : sale.paymentMethod === "pos" ? "POS" : sale.paymentMethod === "iban" ? "IBAN" : "Cari"}
                      </span>
                      <span className="text-xs text-foreground-400">{new Date(sale.date).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })}</span>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-sm font-semibold text-foreground-950">${sale.amount.toLocaleString("en-US", { minimumFractionDigits: 2 })}</p>
                    <p className="text-xs text-foreground-400">{sale.quantity} adet</p>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}